import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Flag, Eye, Trash2, RotateCcw, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import SeasonWrappedCard from './SeasonWrappedCard';

export default function SeasonSheet({ open, onClose, group, data, onChanged }) {
  const [seasons, setSeasons] = useState([]);
  const [name, setName] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [wrapped, setWrapped] = useState(null);

  useEffect(() => {
    if (!open || !group?.id) return;
    setWrapped(null);
    api.get(`/groups/${group.id}/seasons`).then((r) => setSeasons(r.data)).catch(() => {});
    setName(`Season ${(data?.seasons?.length || 0) + 1}`);
  }, [open, group?.id, data?.seasons?.length]);

  const active = seasons.find((s) => s.status === 'active');

  const start = async () => {
    setBusy(true);
    try {
      await api.post(`/groups/${group.id}/seasons`, {
        name, ends_at: endsAt || null, target_sessions: target ? Number(target) : null,
      });
      toast.success(`${name} started — ratings soft-reset`);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start season');
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    if (!window.confirm(`End ${active.name}? Standings will be frozen and awards handed out.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/groups/${group.id}/seasons/${active.id}/end`);
      setWrapped(r.data);
      setSeasons((s) => s.map((x) => (x.id === active.id ? r.data.season : x)));
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not end season');
    } finally {
      setBusy(false);
    }
  };

  const view = async (s) => {
    const r = await api.get(`/groups/${group.id}/seasons/${s.id}/wrapped`);
    setWrapped(r.data);
  };

  const run = async (label, confirmText, fn) => {
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || `${label} done`);
      const list = await api.get(`/groups/${group.id}/seasons`);
      setSeasons(list.data);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const removeSeason = (s) => run('Delete season',
    `Delete ${s.name}? The matches stay, but this season's standings and awards go away for good.`,
    () => api.delete(`/groups/${group.id}/seasons/${s.id}`));

  const resetRatings = () => run('Reset ratings',
    'Put every player back to the starting rating? Match history is kept, but those matches stop counting towards ratings.',
    () => api.post(`/groups/${group.id}/play/reset-ratings`));

  const wipePlay = () => run('Wipe play data',
    'Delete EVERY match, rating, season and league in this group? Money and attendance are untouched. This cannot be undone.',
    () => api.delete(`/groups/${group.id}/play/wipe`));

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[92vh] overflow-y-auto" data-testid="season-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">Seasons</SheetTitle>
          <SheetDescription>Each season soft-resets ratings so everyone has a shot at the podium. Lifetime stays in "All-time".</SheetDescription>
        </SheetHeader>

        {wrapped ? (
          <div className="mt-4 pb-4 space-y-3">
            <SeasonWrappedCard wrapped={wrapped} groupName={group?.name} groupId={group?.id} isAdmin={data?.is_admin} />
            <Button variant="outline" className="rounded-full w-full" onClick={() => setWrapped(null)} data-testid="wrapped-back">Back</Button>
          </div>
        ) : (
          <div className="mt-4 pb-4 space-y-4">
            {active ? (
              <div className="rounded-2xl border-2 border-amber-500/40 p-4" data-testid="active-season-card">
                <p className="text-xs uppercase tracking-wider text-amber-600 dark:text-amber-300 font-bold">Live now</p>
                <p className="font-display font-black text-lg">{active.name}</p>
                <p className="text-xs text-muted-foreground">Started {active.starts_at}{active.ends_at ? ` · ends ${active.ends_at}` : ''}{active.target_sessions ? ` · ${active.target_sessions} sessions` : ''}</p>
                <Button variant="destructive" className="rounded-full mt-3" onClick={end} disabled={busy} data-testid="end-season-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Flag className="w-4 h-4 mr-1" /> End season & wrap up</>}
                </Button>
                {data?.is_admin && (
                  <Button variant="ghost" size="sm" className="rounded-full mt-2 ml-2 h-9 text-xs text-destructive"
                    onClick={() => removeSeason(active)} disabled={busy} data-testid="delete-active-season-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete season
                  </Button>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border p-4 space-y-3" data-testid="new-season-form">
                <p className="font-display text-xs font-bold uppercase tracking-[0.16em]">Start a season</p>
                <div>
                  <Label className="text-xs">Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Monsoon Cup" data-testid="season-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Ends on (optional)</Label>
                    <Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} data-testid="season-ends-input" />
                  </div>
                  <div>
                    <Label className="text-xs"># sessions (optional)</Label>
                    <Input type="number" inputMode="numeric" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="12" data-testid="season-target-input" />
                  </div>
                </div>
                <Button className="rounded-full w-full" onClick={start} disabled={busy || !name.trim()} data-testid="start-season-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start season'}
                </Button>
              </div>
            )}

            {seasons.filter((s) => s.status === 'ended').length > 0 && (
              <div>
                <p className="font-display text-xs font-bold uppercase tracking-[0.16em] mb-2">Hall of fame</p>
                <div className="divide-y divide-border/60 rounded-2xl border">
                  {seasons.filter((s) => s.status === 'ended').map((s) => (
                    <div key={s.id} className="flex items-center gap-2 px-3 py-2.5" data-testid={`past-season-${s.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.starts_at} → {s.ended_at?.slice(0, 10)} · 🥇 {s.awards?.podium?.[0]?.name || '—'}
                        </p>
                      </div>
                      <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => view(s)} data-testid={`view-wrapped-${s.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Wrapped
                      </Button>
                      {data?.is_admin && (
                        <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-destructive"
                          onClick={() => removeSeason(s)} disabled={busy} data-testid={`delete-season-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data?.is_admin && (
              <div className="rounded-2xl border-2 border-destructive/40 bg-destructive/5 p-4 space-y-2" data-testid="play-danger-zone">
                <p className="font-display text-xs font-bold uppercase tracking-[0.16em] text-destructive inline-flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
                </p>
                <p className="text-xs text-muted-foreground">
                  These only touch Play — money, attendance and fines are never affected.
                </p>
                <Button variant="outline" className="rounded-full w-full h-9 text-xs" onClick={resetRatings}
                  disabled={busy} data-testid="reset-ratings-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset everyone to the starting rating
                </Button>
                <Button variant="destructive" className="rounded-full w-full h-9 text-xs" onClick={wipePlay}
                  disabled={busy} data-testid="wipe-play-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Wipe all play data
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
